import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  LocationOn,
  Phone,
  Language,
} from '@mui/icons-material';
import axios from 'axios';
import { ENDPOINTS } from '../../config';

const ClaimLanding = () => {
  const { venueId } = useParams();
  const history = useHistory();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initiating, setInitiating] = useState(false);

  const errorRef = useRef(null);

  // Get claim token from URL query params
  const searchParams = new URLSearchParams(window.location.search);
  const claimToken = searchParams.get('code');

  useEffect(() => {
    document.title = 'Claim Your Venue — Step 1 of 3 | Piatto';
  }, []);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  useEffect(() => {
    fetchVenueDetails();
  }, [venueId]);

  useEffect(() => {
    if (claimToken) {
      axios.post(ENDPOINTS.logClaimPageVisit, { claimToken }).catch(() => {});
    }
  }, [claimToken]);

  const fetchVenueDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(ENDPOINTS.business(venueId));

      if (!response.ok) {
        throw new Error('Venue not found');
      }

      const data = await response.json();
      setVenue(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching venue:', err);
      setError('Unable to load venue details. Please check the link and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateClaim = async () => {
    if (!claimToken) {
      setError('Invalid claim link. Please use the link from your email.');
      return;
    }

    try {
      setInitiating(true);
      setError(null);

      const response = await axios.post(ENDPOINTS.initiateClaim, { claimToken });

      const { sessionId } = response.data;
      // Link click is sufficient verification — go directly to account creation
      history.push(`/claim-venue/${venueId}/create-account`, {
        sessionId,
        venue: response.data.venue,
      });
    } catch (err) {
      console.error('Error initiating claim:', err);
      setError(
        err.response?.data?.error ||
        'Failed to initiate claim. Please try again or contact support.'
      );
    } finally {
      setInitiating(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress size={60} aria-label="Loading venue details" />
        <Typography sx={{ mt: 2 }}>Loading venue details...</Typography>
      </Container>
    );
  }

  if (error && !venue) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error" role="alert">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Claim Your Venue
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Welcome to Piatto! Confirm your venue details and create your account.
        </Typography>
      </Box>

      {/* Venue Information Card */}
      <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <CheckCircle
              sx={{ fontSize: 40, color: 'success.main', mr: 2 }}
              aria-hidden="true"
            />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {venue?.bizName || 'Your Venue'}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            {venue?.bizAddress && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <LocationOn sx={{ mr: 1, color: 'text.secondary' }} aria-hidden="true" />
                <Typography>{venue.bizAddress}</Typography>
              </Box>
            )}

            {venue?.bizPhone && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Phone sx={{ mr: 1, color: 'text.secondary' }} aria-hidden="true" />
                <Typography>{venue.bizPhone}</Typography>
              </Box>
            )}

            {venue?.bizWeb && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Language sx={{ mr: 1, color: 'text.secondary' }} aria-hidden="true" />
                <Typography>{venue.bizWeb}</Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Claim action */}
      <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              role="alert"
              ref={errorRef}
              tabIndex={-1}
            >
              {error}
            </Alert>
          )}

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            By clicking the link in our email, you've confirmed access to this
            venue's inbox — thank you. Click below to set up your Piatto account and connect
            it to <strong>{venue?.bizName || 'your venue'}</strong>.
          </Typography>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleInitiateClaim}
              disabled={initiating || !claimToken}
              aria-busy={initiating}
              sx={{
                py: 1.5,
                px: 6,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              {initiating ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 2 }} aria-hidden="true" />
                  Starting…
                </>
              ) : (
                'Claim This Venue'
              )}
            </Button>

            {!claimToken && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }} role="alert">
                Invalid claim link. Please use the link from your email.
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Not the right venue, or need help?{' '}
              <a href="mailto:hello@piatto.com.au" style={{ color: '#B91C1C' }}>
                Contact us at hello@piatto.com.au
              </a>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Claiming is free and takes less than 2 minutes.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Questions? Visit{' '}
          <a href="https://piatto.com.au" style={{ color: '#B91C1C' }}>
            piatto.com.au
          </a>
        </Typography>
      </Box>
    </Container>
  );
};

export default ClaimLanding;
